import * as React from 'react';
import { cx } from '../tone';

export interface SkeletonProps {
  /** Shape. @default 'line' */
  variant?: 'line' | 'block' | 'card';
  /** Width (px number or any CSS length). */
  width?: number | string;
  /** Height (px number or any CSS length). */
  height?: number | string;
  /** Corner radius (px number or any CSS length). */
  radius?: number | string;
  /** Extra classes on the root. */
  className?: string;
  /** Inline style on the root. */
  style?: React.CSSProperties;
}

/**
 * `Skeleton` — glass shimmer placeholder. A single highlight sweeps
 * left→right. Mirror the real content's geometry so nothing jumps on load.
 * Decorative by design (`aria-hidden`) — the surrounding loading region owns
 * the `role="status"` + screen-reader label.
 */
export function Skeleton({
  variant = 'line',
  width,
  height,
  radius,
  className,
  style,
}: SkeletonProps): React.ReactElement {
  if (variant === 'card') {
    return (
      <div
        className={cx('lm-sk-card', className)}
        style={{ width: width ?? 220, ...style }}
        aria-hidden="true"
      >
        <div className="lm-sk" style={{ height: height ?? 120 }} />
        <div className="lm-sk-card__body">
          <div className="lm-sk lm-sk--line lm-sk-card__line--1" />
          <div className="lm-sk lm-sk--line lm-sk-card__line--2" />
          <div className="lm-sk lm-sk--line lm-sk-card__line--3" />
        </div>
      </div>
    );
  }

  const isLine = variant === 'line';
  return (
    <div
      className={cx('lm-sk', isLine && 'lm-sk--line', className)}
      style={{
        width: width ?? (isLine ? '100%' : 80),
        height: height ?? (isLine ? 12 : 80),
        borderRadius: radius,
        ...style,
      }}
      aria-hidden="true"
    />
  );
}
